/**
 * User Forms Panel
 * 
 * Shows forms attached to a specific user with:
 * - Question-based view (not raw fields)
 * - Automatic pre-fill from existing user data
 * - Manual answer input by expert
 * - Generate link for user to complete
 * - Progress tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';
import { useToast } from '../../../Context/ToastContext';
import { useFeature } from '../../../Context/CapabilityContext';
import { FaLink, FaCheck, FaCopy, FaPlus, FaChevronDown, FaChevronRight, FaUser, FaRobot, FaSync, FaFilePdf, FaFileArchive, FaDownload, FaFileAlt, FaClipboardList, FaTrashAlt, FaExclamationTriangle } from 'react-icons/fa';
import { IoCheckmarkCircle, IoTimeOutline, IoCloseCircle } from 'react-icons/io5';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #0a0d14;
  color: #e8eaed;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 16px 20px;
  background: rgba(17, 24, 39, 0.98);
  border-bottom: 1px solid rgba(75, 85, 99, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? css`
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: white;
    
    &:hover:not(:disabled) {
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    }
  ` : css`
    background: rgba(55, 65, 81, 0.5);
    border: 1px solid rgba(75, 85, 99, 0.5);
    color: #d1d5db;
    
    &:hover:not(:disabled) {
      background: rgba(75, 85, 99, 0.5);
      border-color: rgba(99, 102, 241, 0.5);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 280px;
  min-width: 280px;
  border-right: 1px solid rgba(75, 85, 99, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid rgba(75, 85, 99, 0.2);
  background: rgba(17, 24, 39, 0.5);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SidebarTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #a5b4fc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FormsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const FormCard = styled.div`
  padding: 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 8px;
  position: relative;
  
  background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.15)' : 'rgba(31, 41, 55, 0.4)'};
  border: 1px solid ${props => props.$selected ? 'rgba(99, 102, 241, 0.4)' : 'rgba(75, 85, 99, 0.2)'};
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.3);
  }
  
  &:hover .detach-btn {
    opacity: 1;
  }
`;

const OutdatedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #fbbf24;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.35);
`;

const DetachButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.3);
    transform: scale(1.1);
  }
  
  svg {
    font-size: 11px;
  }
`;

const FormCardName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #e8eaed;
  margin-bottom: 8px;
`;

const FormCardProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background: rgba(55, 65, 81, 0.5);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.$percent >= 100 ? '#10b981' : 'linear-gradient(90deg, #6366f1, #8b5cf6)'};
  border-radius: 2px;
  width: ${props => props.$percent}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.span`
  font-size: 11px;
  color: ${props => props.$percent >= 100 ? '#10b981' : '#9ca3af'};
  min-width: 35px;
  text-align: right;
`;

const MainPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FormHeader = styled.div`
  padding: 16px 20px;
  background: rgba(17, 24, 39, 0.5);
  border-bottom: 1px solid rgba(75, 85, 99, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FormTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #fff;
`;

const FormStats = styled.div`
  display: flex;
  gap: 16px;
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${props => props.$color || '#9ca3af'};
`;

const QuestionsContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* generous bottom padding so the last question card is never clipped/hidden behind the footer */
  padding: 20px 20px 96px;
`;

const SectionCard = styled.div`
  background: rgba(17, 24, 39, 0.6);
  border: 1px solid rgba(75, 85, 99, 0.3);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 14px 18px;
  background: rgba(31, 41, 55, 0.5);
  border-bottom: 1px solid rgba(75, 85, 99, 0.2);
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  
  &:hover {
    background: rgba(55, 65, 81, 0.4);
  }
`;

const SectionTitle = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #e8eaed;
`;

const SectionProgress = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

const SectionContent = styled.div`
  padding: ${props => props.$expanded ? '12px' : '0'};
  max-height: ${props => props.$expanded ? '2000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
`;

const QuestionRow = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  background: ${props => props.$answered ? 'rgba(16, 185, 129, 0.08)' : 'rgba(31, 41, 55, 0.4)'};
  border: 1px solid ${props => props.$answered ? 'rgba(16, 185, 129, 0.2)' : 'rgba(75, 85, 99, 0.2)'};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.3);
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
`;

const QuestionStatus = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  
  ${props => props.$answered ? css`
    background: rgba(16, 185, 129, 0.2);
    color: #10b981;
  ` : css`
    background: rgba(245, 158, 11, 0.2);
    color: #f59e0b;
  `}
  
  svg {
    font-size: 10px;
  }
`;

const QuestionText = styled.div`
  flex: 1;
  font-size: 13px;
  color: #e8eaed;
  line-height: 1.5;
`;

const AnswerInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  background: rgba(17, 24, 39, 0.8);
  border: 1px solid rgba(75, 85, 99, 0.4);
  border-radius: 8px;
  color: #e8eaed;
  font-size: 13px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
    background: rgba(17, 24, 39, 1);
  }
  
  &::placeholder {
    color: rgba(156, 163, 175, 0.6);
  }
`;

const AnswerDisplay = styled.div`
  padding: 10px 14px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 8px;
  color: #10b981;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldName = styled.div`
  font-size: 10px;
  color: rgba(156, 163, 175, 0.6);
  margin-top: 6px;
  font-family: monospace;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  padding: 40px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyText = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
`;

const EmptySubtext = styled.div`
  font-size: 12px;
  color: #4b5563;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid rgba(99, 102, 241, 0.2);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #1f2937;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 70vh;
  overflow-y: auto;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 20px 0;
`;

const TemplateOption = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  cursor: pointer;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  
  background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.2)' : 'rgba(31, 41, 55, 0.5)'};
  border: 1px solid ${props => props.$selected ? 'rgba(99, 102, 241, 0.5)' : 'rgba(75, 85, 99, 0.3)'};
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const TemplateName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #e8eaed;
`;

const TemplateDetails = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const LinkDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(17, 24, 39, 0.8);
  border: 1px solid rgba(75, 85, 99, 0.4);
  border-radius: 8px;
  margin-top: 16px;
`;

const LinkText = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: #a5b4fc;
  font-size: 12px;
  font-family: monospace;
  
  &:focus {
    outline: none;
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: rgba(99, 102, 241, 0.2);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  color: #a5b4fc;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.3);
  }
`;

const ConfirmationModal = styled.div`
  background: #1f2937;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 420px;
  animation: ${fadeIn} 0.2s ease;
`;

const WarningIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  
  svg {
    font-size: 24px;
  }
`;

const ConfirmTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 12px 0;
  text-align: center;
`;

const ConfirmText = styled.p`
  font-size: 14px;
  color: #9ca3af;
  margin: 0 0 8px 0;
  text-align: center;
  line-height: 1.5;
`;

const FormNameHighlight = styled.span`
  color: #a5b4fc;
  font-weight: 500;
`;

const DangerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  color: white;
  
  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ============================================
// COMPONENT
// ============================================

const UserFormsPanel = ({ selectedUser, threadId, conversationId }) => {
  // Use conversationId or threadId - this is the key for per-case forms
  const caseId = conversationId || threadId || selectedUser?._id || selectedUser?.threadId;
  const axios = useAuthenticatedAxios();
  const { success, error: showError } = useToast();

  // Capability gating: hide immigration/IRCC-specific actions for non-immigration experts
  const showIrccPackage = useFeature('irccForms');
  const showPortalAutofill = useFeature('portalAutofill');

  // State
  const [userForms, setUserForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formDetails, setFormDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Modal state
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]); // multi-select in the attach modal
  const [attaching, setAttaching] = useState(false);
  
  // Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  
  // Refresh state
  const [refreshing, setRefreshing] = useState(false);
  
  // Package generation state
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [applicationTypes, setApplicationTypes] = useState([]);
  const [selectedAppType, setSelectedAppType] = useState(null);
  const [generatingPackage, setGeneratingPackage] = useState(false);
  // Selection of which forms + documents to include in the package
  const [selectedFormIds, setSelectedFormIds] = useState(new Set());
  const [caseFiles, setCaseFiles] = useState([]);
  const [selectedDocKeys, setSelectedDocKeys] = useState(new Set());
  const [loadingCaseFiles, setLoadingCaseFiles] = useState(false);
  
  // Detach confirmation state
  const [showDetachModal, setShowDetachModal] = useState(false);
  const [formToDetach, setFormToDetach] = useState(null);
  const [detaching, setDetaching] = useState(false);
  
  // Answer changes
  const [pendingAnswers, setPendingAnswers] = useState({});
  
  // User identifier (email) - handle different property names and nested structures
  const getUserEmail = () => {
    if (!selectedUser) return null;
    
    // Direct properties
    if (selectedUser.email) return selectedUser.email;
    if (selectedUser.userEmail) return selectedUser.userEmail;
    if (selectedUser.mail) return selectedUser.mail;
    if (selectedUser.customerEmail) return selectedUser.customerEmail;
    
    // Check users array (conversation model)
    // The conversation model has users: [{name, id, type}] - name might be email
    if (selectedUser.users && Array.isArray(selectedUser.users)) {
      // First try to find user with type 'user' (not 'expert' or 'gpt')
      const regularUser = selectedUser.users.find(u => u.type === 'user');
      if (regularUser) {
        // Check if user has email property
        if (regularUser.email) return regularUser.email;
        // Check if name contains @ (name might be email)
        if (regularUser.name && regularUser.name.includes('@')) return regularUser.name;
        // Check if id contains @ (id might be email)
        if (regularUser.id && regularUser.id.includes('@')) return regularUser.id;
      }
      
      // Otherwise find any user with email in name
      const userWithEmailName = selectedUser.users.find(u => u.name && u.name.includes('@'));
      if (userWithEmailName?.name) return userWithEmailName.name;
    }
    
    // Check customer object
    if (selectedUser.customer?.email) return selectedUser.customer.email;
    
    // Check for participants array
    if (selectedUser.participants && Array.isArray(selectedUser.participants)) {
      const participant = selectedUser.participants.find(p => p.email && p.role !== 'expert');
      if (participant?.email) return participant.email;
    }
    
    // Check for user.name containing @ (might be email used as name)
    if (selectedUser.name && selectedUser.name.includes('@')) return selectedUser.name;
    if (selectedUser.userName && selectedUser.userName.includes('@')) return selectedUser.userName;
    
    // Check displayName
    if (selectedUser.displayName && selectedUser.displayName.includes('@')) return selectedUser.displayName;
    
    // Check lastAnalysis.data.clientProfile.email (extracted by AI analysis)
    if (selectedUser.lastAnalysis?.data?.clientProfile?.email) return selectedUser.lastAnalysis.data.clientProfile.email;
    
    return null;
  };
  
  const userEmail = getUserEmail();
  
  // Get a unique ID for the selected user to track changes
  const selectedUserId = selectedUser?._id || selectedUser?.threadId || selectedUser?.id || null;
  
  // Debug: log what we have
  useEffect(() => {
    console.log('[UserFormsPanel] selectedUser:', selectedUser);
    console.log('[UserFormsPanel] selectedUserId:', selectedUserId);
    console.log('[UserFormsPanel] userEmail:', userEmail);
    console.log('[UserFormsPanel] selectedUser keys:', selectedUser ? Object.keys(selectedUser) : 'null');
  }, [selectedUser, selectedUserId, userEmail]);
  
  // Load forms for this conversation/case
  const loadUserForms = useCallback(async () => {
    if (!caseId) {
      console.log('[UserFormsPanel] No caseId (conversationId), skipping load');
      setLoading(false);
      return;
    }
    
    try {
      console.log('[UserFormsPanel] Loading forms for case:', caseId);
      const response = await axios.get(`/form-filling/user-sessions`, {
        params: { conversationId: caseId, _t: Date.now() }  // _t busts any stale HTTP cache
      });
      
      console.log('[UserFormsPanel] Response:', response.data);
      
      if (response.data.success) {
        setUserForms(response.data.sessions || []);
        // Select first form if available
        if (response.data.sessions?.length > 0) {
          setSelectedForm(response.data.sessions[0]);
        }
      }
    } catch (error) {
      console.error('[UserFormsPanel] Error loading forms:', error);
    } finally {
      setLoading(false);
    }
  }, [axios, caseId]);
  
  // Load available templates
  const loadTemplates = useCallback(async () => {
    try {
      const response = await axios.get('/document-templates');
      if (response.data.success) {
        // Filter to only show templates with questionnaires
        const withQuestionnaires = response.data.templates.filter(t => t.questionnaireId);
        setTemplates(withQuestionnaires);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  }, [axios]);
  
  // Load form details (questions)
  const loadFormDetails = useCallback(async (sessionId) => {
    if (!sessionId) return;
    
    setLoadingDetails(true);
    try {
      const response = await axios.get(`/form-filling/sessions/${sessionId}/details`, {
        params: { _t: Date.now() }  // _t busts any stale HTTP cache
      });
      if (response.data.success) {
        setFormDetails(response.data);
        // Expand first section by default
        if (response.data.sections?.length > 0) {
          setExpandedSections({ [response.data.sections[0].title]: true });
        }
      }
    } catch (error) {
      console.error('Error loading form details:', error);
    } finally {
      setLoadingDetails(false);
    }
  }, [axios]);
  
  // Reset and reload when conversation changes
  useEffect(() => {
    // Reset all state when conversation/case changes
    setLoading(true);
    setSelectedForm(null);
    setFormDetails(null);
    setUserForms([]);
    setPendingAnswers({});
    setExpandedSections({});
    
    if (caseId) {
      console.log('[UserFormsPanel] Case changed, loading forms for caseId:', caseId);
      loadUserForms();
      loadTemplates();
    } else {
      setLoading(false);
    }
  }, [caseId]); // Depend on caseId (conversationId)
  
  useEffect(() => {
    if (selectedForm?.sessionId) {
      loadFormDetails(selectedForm.sessionId);
    }
  }, [selectedForm?.sessionId, loadFormDetails]);
  
  // Attach new form to this conversation/case
  const toggleTemplateSelected = (id) => {
    setSelectedTemplateIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Attach a single template: create the form-filling session + email the client.
  // Returns the created session; throws on failure (caller aggregates results).
  const attachOneForm = async (template) => {
    const response = await axios.post('/form-filling/sessions', {
      formTemplateId: template._id,
      userEmail: userEmail,
      userName: selectedUser?.name || selectedUser?.userName,
      conversationId: caseId  // Link form to this specific conversation/case
    });
    if (!response.data?.success) throw new Error('Attach failed');
    const { sessionUrl, accessPassword, session } = response.data;

    // Send email with link and password (non-fatal if it fails)
    try {
      await fetch('https://mailstrom-cz3mc.ondigitalocean.app/meetings/form-filling', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          userName: selectedUser?.name || selectedUser?.userName || 'Valued Client',
          expertName: 'Your Advisor',
          formName: template?.name || 'Application Form',
          formLink: sessionUrl,
          accessPassword: accessPassword,
          expiresAt: new Date(session.expiresAt).toLocaleDateString()
        })
      });
    } catch (emailErr) {
      console.error('Failed to send email:', emailErr);
    }
    return { session, accessPassword };
  };

  // Attach every selected template (skips ones already attached), then summarize.
  const handleAttachForms = async () => {
    if (!selectedTemplateIds.length || !userEmail || !caseId) {
      showError('Select at least one form to attach');
      return;
    }

    const toAttach = templates.filter(t =>
      selectedTemplateIds.includes(t._id) && !userForms.some(f => f.formTemplateId === t._id)
    );
    if (!toAttach.length) {
      showError('Selected form(s) are already attached');
      return;
    }

    setAttaching(true);
    let attached = 0;
    const failed = [];
    let lastSession = null;
    for (const template of toAttach) {
      try {
        const { session } = await attachOneForm(template);
        attached++;
        lastSession = session;
      } catch (err) {
        if (err.response?.status !== 409) {
          console.error('Error attaching form:', err);
          failed.push(template.formCode || template.name);
        }
      }
    }
    setAttaching(false);

    await loadUserForms();
    if (attached > 0) {
      success(`Attached ${attached} form${attached > 1 ? 's' : ''}${failed.length ? ` · ${failed.length} failed` : ''} — link${attached > 1 ? 's' : ''} emailed to ${userEmail}`);
    } else {
      showError(`Failed to attach form(s)${failed.length ? `: ${failed.join(', ')}` : ''}`);
    }

    setShowAttachModal(false);
    setSelectedTemplateIds([]);
    if (lastSession) {
      setSelectedForm({ sessionId: lastSession.sessionId, ...lastSession });
    }
  };
  
  // Generate link for user and resend email - now uses password-protected form on Expert Platform
  const handleGenerateLink = async () => {
    if (!selectedForm?.sessionId) return;
    
    setGeneratingLink(true);
    try {
      // Get session info with password from backend
      const response = await axios.get(`/form-filling/sessions/${selectedForm.sessionId}/resend-info`);
      
      if (response.data.success) {
        const { session, sessionUrl } = response.data;
        setGeneratedLink(sessionUrl);
        
        // Send email with link and password
        try {
          await fetch('https://mailstrom-cz3mc.ondigitalocean.app/meetings/form-filling', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: session.userEmail,
              userName: session.userName || 'Valued Client',
              expertName: 'Your Advisor',
              formName: selectedForm?.formName || 'Application Form',
              formLink: sessionUrl,
              accessPassword: session.accessPassword,
              expiresAt: new Date(session.expiresAt).toLocaleDateString()
            })
          });
          success(`📧 Email sent to ${session.userEmail} with link and password!`);
        } catch (emailErr) {
          console.error('Failed to send email:', emailErr);
          showError('Link generated but email failed to send');
        }
        
        setShowLinkModal(true);
      } else {
        showError(response.data.error || 'Failed to get session info');
      }
    } catch (error) {
      console.error('Error generating link:', error);
      showError('Failed to generate link');
    } finally {
      setGeneratingLink(false);
    }
  };
  
  // Copy link to clipboard
  const copyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    success('Link copied to clipboard!');
  };
  
  // Refresh form data from database using AI profile matching
  const handleRefresh = async () => {
    if (!selectedForm?.sessionId) return;

    setRefreshing(true);
    try {
      // 1) ALWAYS reload the latest submitted data first, so answers the user
      //    just submitted appear immediately — independent of the (slower,
      //    optional) AI profile match below. Previously the reload was gated
      //    behind the AI call, so if that hung/failed the panel never updated.
      await loadFormDetails(selectedForm.sessionId);
      await loadUserForms();

      // 2) Then run AI profile matching as a best-effort enhancement. If it's
      //    slow or unavailable it must not block showing the submitted data.
      try {
        const refreshResponse = await axios.post(`/form-filling/sessions/${selectedForm.sessionId}/refresh`);
        if (refreshResponse.data?.success) {
          const { newDataFound, matchedFields, missingFields } = refreshResponse.data;
          if (newDataFound > 0) {
            // AI found extra data — reload again to reflect it.
            await loadFormDetails(selectedForm.sessionId);
            await loadUserForms();
            success(`Updated. AI matched ${newDataFound} field(s) from user profile.`);
            console.log('[UserFormsPanel] Matched fields:', matchedFields);
          } else if (missingFields?.length > 0) {
            success(`Updated. ${missingFields.length} field(s) still need to be collected.`);
          } else {
            success('Form data updated.');
          }
        } else {
          success('Form data updated.');
        }
      } catch (aiErr) {
        // AI match unavailable — the submitted data is already shown from step 1.
        console.warn('[UserFormsPanel] AI profile match skipped:', aiErr?.message);
        success('Form data updated.');
      }
    } catch (error) {
      console.error('[UserFormsPanel] Error refreshing:', error);
      showError('Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  // Download the filled PDF. Validates first and surfaces issues so the adviser
  // never unknowingly sends an incomplete/incorrect or blank form. `force` skips
  // the gates (used when the adviser explicitly chooses to proceed anyway).
  const handleDownloadPdf = async (force = false) => {
    const sessionId = selectedForm?.sessionId;
    if (!sessionId) return;
    const baseUrl = (process.env.REACT_APP_SERVER || 'http://localhost:3030').replace(/\/$/, '');

    try {
      // Pre-validate (best-effort) so we can warn before downloading.
      if (!force) {
        try {
          const vRes = await fetch(`${baseUrl}/form-filling/sessions/${sessionId}/validate`);
          const vJson = await vRes.json().catch(() => null);
          const v = vJson?.validation;
          if (v && !v.valid) {
            const invalidList = v.invalidOptions || [];
            const missingList = v.missingRequired || [];
            const lines = [];
            for (const iss of invalidList.slice(0, 12)) {
              lines.push(`• ${iss.question || iss.fieldName}: "${iss.value}" is not an accepted option`);
            }
            for (const iss of missingList.slice(0, 12)) {
              lines.push(`• ${iss.question || iss.fieldName}: required, still empty`);
            }
            const extra = (invalidList.length + missingList.length) - lines.length;
            const proceed = window.confirm(
              `These answers need attention:\n\n${lines.join('\n')}${extra > 0 ? `\n…and ${extra} more` : ''}\n\n` +
              (invalidList.length ? 'Invalid answers cannot be written to the PDF and will be left blank.\n\n' : '') +
              `Download anyway?`
            );
            if (!proceed) return;
            force = true;
          }
        } catch (e) { /* validation is best-effort; continue to download */ }
      }

      const url = `${baseUrl}/form-filling/sessions/${sessionId}/pdf${force ? '?force=true' : ''}`;
      const response = await fetch(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const code = errorData?.code;
        if (code === 'FORM_OUTDATED') {
          if (window.confirm(`${errorData.error}\n\nDownload the current version anyway?`)) {
            return handleDownloadPdf(true);
          }
          return;
        }
        if (code === 'VALIDATION_FAILED') {
          if (window.confirm(`${errorData.error}\n\nDownload anyway?`)) {
            return handleDownloadPdf(true);
          }
          return;
        }
        if (code === 'FORM_NOT_FILLABLE') {
          showError(errorData.error || 'This form cannot be auto-filled. Please fill it manually.');
          return;
        }
        showError(errorData?.error || 'Failed to generate PDF');
        return;
      }

      const blob = await response.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      const disposition = response.headers.get('Content-Disposition');
      const fileName = disposition?.match(/filename="(.+)"/)?.[1] || 'filled_form.pdf';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objUrl);

      const filled = response.headers.get('X-PDF-Filled');
      const total = response.headers.get('X-PDF-Total');
      const skipped = response.headers.get('X-PDF-Skipped');
      const invalid = response.headers.get('X-PDF-Invalid-Options');
      if (filled !== null && total !== null) {
        const filledNum = parseInt(filled);
        const totalNum = parseInt(total);
        const skippedNum = parseInt(skipped || '0');
        const invalidNum = parseInt(invalid || '0');
        if (filledNum === 0 && totalNum > 0) {
          showError('PDF downloaded but no fields were filled. Check field mappings.');
        } else if (invalidNum > 0) {
          success(`PDF downloaded: ${filledNum} fields filled (${invalidNum} answer(s) skipped as invalid options)`);
        } else if (skippedNum > 0) {
          success(`PDF downloaded: ${filledNum} of ${filledNum + skippedNum} fields filled (${skippedNum} unmatched)`);
        } else {
          success(`PDF downloaded: ${filledNum} fields filled successfully`);
        }
      } else {
        success('PDF downloaded successfully');
      }
    } catch (err) {
      showError('Failed to download PDF. Please try again.');
    }
  };

  // Load IRCC application types
  const loadApplicationTypes = useCallback(async () => {
    try {
      const response = await axios.get('/ircc/application-types');
      if (response.data.success) {
        setApplicationTypes(response.data.applicationTypes);
      }
    } catch (error) {
      console.error('[UserFormsPanel] Error loading application types:', error);
    }
  }, [axios]);
  
  // Load the case's uploaded documents (from Spaces, via conversation files)
  const loadCaseFiles = useCallback(async () => {
    const tId = threadId || selectedUser?.threadId;
    if (!tId) { setCaseFiles([]); return; }
    setLoadingCaseFiles(true);
    try {
      const res = await axios.get(`/conversation-files/${encodeURIComponent(tId)}`);
      const files = res.data?.files || [];
      setCaseFiles(files);
      // Default: select all documents
      setSelectedDocKeys(new Set(files.map(f => f.fileName)));
    } catch (e) {
      console.error('[UserFormsPanel] Error loading case files:', e);
      setCaseFiles([]);
    } finally {
      setLoadingCaseFiles(false);
    }
  }, [axios, threadId, selectedUser]);

  // Open package generation modal
  const handleOpenPackageModal = () => {
    loadApplicationTypes();
    loadCaseFiles();
    // Default: include all attached forms
    setSelectedFormIds(new Set(userForms.map(f => f.sessionId)));
    setShowPackageModal(true);
  };

  const toggleFormSelection = (sessionId) => {
    setSelectedFormIds(prev => {
      const next = new Set(prev);
      next.has(sessionId) ? next.delete(sessionId) : next.add(sessionId);
      return next;
    });
  };

  const toggleDocSelection = (fileKey) => {
    setSelectedDocKeys(prev => {
      const next = new Set(prev);
      next.has(fileKey) ? next.delete(fileKey) : next.add(fileKey);
      return next;
    });
  };
  
  // Generate submission package
  const handleGeneratePackage = async () => {
    if (!selectedAppType) return;
    const chosenForms = userForms.filter(f => selectedFormIds.has(f.sessionId));
    const chosenDocs = caseFiles
      .filter(f => selectedDocKeys.has(f.fileName))
      .map(f => ({ fileKey: f.fileName, originalName: f.originalName, downloadUrl: f.downloadUrl }));
    if (chosenForms.length === 0 && chosenDocs.length === 0) {
      showError('Select at least one form or document to include');
      return;
    }

    setGeneratingPackage(true);
    try {
      // Get applicant info from the selected user
      const firstName = selectedUser?.name?.split(' ')[0] || 
                       selectedUser?.userName?.split(' ')[0] || 
                       'Unknown';
      const lastName = selectedUser?.name?.split(' ').slice(1).join(' ') || 
                      selectedUser?.userName?.split(' ').slice(1).join(' ') || 
                      'User';
      
      const response = await axios.post('/ircc/generate-package', {
        typeId: selectedAppType.typeId,
        conversationId: caseId,
        sessionIds: chosenForms.map(f => f.sessionId),
        documents: chosenDocs,
        applicantInfo: {
          firstName,
          lastName,
          applicationId: caseId
        }
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedAppType.name.replace(/\s+/g, '_')}_${lastName}_${firstName}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      success('Submission package downloaded!');
      setShowPackageModal(false);
    } catch (error) {
      console.error('[UserFormsPanel] Error generating package:', error);
      showError('Failed to generate submission package');
    } finally {
      setGeneratingPackage(false);
    }
  };
  
  // Open detach confirmation modal
  const handleDetachClick = (e, form) => {
    e.stopPropagation(); // Prevent selecting the form
    setFormToDetach(form);
    setShowDetachModal(true);
  };
  
  // Confirm and detach form from conversation
  const handleConfirmDetach = async () => {
    if (!formToDetach?.sessionId) return;
    
    setDetaching(true);
    try {
      const response = await axios.delete(`/form-filling/sessions/${formToDetach.sessionId}`, {
        data: { confirmed: true }
      });
      
      if (response.data.success) {
        success(response.data.message || 'Form detached successfully');
        
        // If the detached form was selected, clear selection
        if (selectedForm?.sessionId === formToDetach.sessionId) {
          setSelectedForm(null);
          setFormDetails(null);
        }
        
        // Reload forms list
        await loadUserForms();
        
        setShowDetachModal(false);
        setFormToDetach(null);
      } else {
        showError(response.data.error || 'Failed to detach form');
      }
    } catch (error) {
      console.error('[UserFormsPanel] Error detaching form:', error);
      showError('Failed to detach form');
    } finally {
      setDetaching(false);
    }
  };
  
  // Handle answer change
  const handleAnswerChange = (fieldName, value) => {
    setPendingAnswers(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };
  
  // Save manual answers
  const handleSaveAnswers = async () => {
    if (!selectedForm?.sessionId || Object.keys(pendingAnswers).length === 0) return;
    
    setSaving(true);
    try {
      const response = await axios.post(`/form-filling/sessions/${selectedForm.sessionId}/manual-answers`, {
        answers: Object.entries(pendingAnswers).map(([fieldName, value]) => ({
          fieldName,
          value
        }))
      });
      
      if (response.data.success) {
        success(`Saved ${Object.keys(pendingAnswers).length} answers`);
        setPendingAnswers({});
        // Reload form details
        await loadFormDetails(selectedForm.sessionId);
        // Reload user forms to update progress
        await loadUserForms();
      }
    } catch (error) {
      console.error('Error saving answers:', error);
      showError('Failed to save answers');
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle section expansion
  const toggleSection = (title) => {
    setExpandedSections(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };
  
  // Get answer for a field
  const getAnswer = (fieldName) => {
    // Check pending answers first
    if (pendingAnswers[fieldName] !== undefined) {
      return pendingAnswers[fieldName];
    }
    // Then check form details
    return formDetails?.answers?.[fieldName] || '';
  };
  
  // Render empty state - no case selected
  if (!caseId) {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon><FaClipboardList /></EmptyIcon>
          <EmptyText>Select a case to view forms</EmptyText>
          <EmptySubtext>
            Click on a user/case in the list to see attached forms
          </EmptySubtext>
        </EmptyState>
      </Container>
    );
  }
  
  // Render empty state - no user email (needed for form filling)
  if (!userEmail) {
    // Debug info for troubleshooting
    const users = selectedUser?.users;
    const userNames = users ? users.map(u => `${u.type}:${u.name}`).join(', ') : 'no users array';
    const debugInfo = `Case ID: ${caseId}. Keys: ${Object.keys(selectedUser || {}).join(', ')}. Users: [${userNames}]`;
    
    return (
      <Container>
        <EmptyState>
          <EmptyIcon><FaClipboardList /></EmptyIcon>
          <EmptyText>No user email found for this case</EmptyText>
          <EmptySubtext style={{ maxWidth: '400px', wordBreak: 'break-all' }}>
            {debugInfo}
          </EmptySubtext>
        </EmptyState>
      </Container>
    );
  }
  
  return (
    <Container>
      <Header>
        <HeaderTitle>
          <FaFileAlt style={{ fontSize: '14px' }} /> User Forms
        </HeaderTitle>
        <HeaderActions>
          {Object.keys(pendingAnswers).length > 0 && (
            <Button $primary onClick={handleSaveAnswers} disabled={saving}>
              {saving ? <LoadingSpinner /> : <FaCheck />}
              Save {Object.keys(pendingAnswers).length} Answers
            </Button>
          )}
        </HeaderActions>
      </Header>
      
      <Content>
        {/* Sidebar - Forms List */}
        <Sidebar>
          <SidebarHeader>
            <SidebarTitle>Attached Forms</SidebarTitle>
            <Button onClick={() => { setSelectedTemplateIds([]); setShowAttachModal(true); }}>
              <FaPlus /> New
            </Button>
          </SidebarHeader>
          
          {loading ? (
            <LoadingContainer>
              <LoadingSpinner />
            </LoadingContainer>
          ) : (
            <FormsList>
              {userForms.length === 0 ? (
                <EmptyState style={{ padding: '20px', minHeight: 'auto' }}>
                  <EmptyText style={{ fontSize: '12px' }}>No forms attached</EmptyText>
                  <EmptySubtext style={{ fontSize: '11px' }}>Click "New" to attach a form</EmptySubtext>
                </EmptyState>
              ) : (
                userForms.map(form => (
                  <FormCard 
                    key={form.sessionId}
                    $selected={selectedForm?.sessionId === form.sessionId}
                    onClick={() => setSelectedForm(form)}
                  >
                    <DetachButton 
                      className="detach-btn"
                      onClick={(e) => handleDetachClick(e, form)}
                      title="Detach form from this case"
                    >
                      <FaTrashAlt />
                    </DetachButton>
                    <FormCardName>{form.formName || 'Untitled Form'}</FormCardName>
                    {form.formOutdated && (
                      <OutdatedBadge title="The underlying IRCC form changed. Re-import the latest version before finalizing the PDF.">
                        ⚠️ Form outdated
                      </OutdatedBadge>
                    )}
                    <FormCardProgress>
                      <ProgressBar>
                        <ProgressFill $percent={form.completionPercent || 0} />
                      </ProgressBar>
                      <ProgressText $percent={form.completionPercent || 0}>
                        {form.completionPercent || 0}%
                      </ProgressText>
                    </FormCardProgress>
                  </FormCard>
                ))
              )}
            </FormsList>
          )}
        </Sidebar>
        
        {/* Main Panel - Questions */}
        <MainPanel>
          {!selectedForm ? (
            <EmptyState>
              <EmptyIcon>📝</EmptyIcon>
              <EmptyText>Select a form to view questions</EmptyText>
            </EmptyState>
          ) : loadingDetails ? (
            <LoadingContainer>
              <LoadingSpinner />
            </LoadingContainer>
          ) : (
            <>
              <FormHeader>
                <FormTitle>{formDetails?.formName || selectedForm.formName || 'Form'}</FormTitle>
                <FormStats>
                  <Stat $color="#10b981">
                    <IoCheckmarkCircle /> {formDetails?.answeredCount || 0} Answered
                  </Stat>
                  <Stat $color="#f59e0b">
                    <IoTimeOutline /> {formDetails?.remainingCount || 0} Remaining
                  </Stat>
                  <Button onClick={handleRefresh} disabled={refreshing}>
                    <FaSync style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} /> Update
                  </Button>
                  {showPortalAutofill && (
                    <Button
                      onClick={() => handleDownloadPdf()}
                      disabled={!formDetails?.answeredCount}
                      title="Fill PDF with collected data"
                    >
                      <FaFilePdf /> Fill PDF
                    </Button>
                  )}
                  <Button onClick={handleGenerateLink} disabled={generatingLink}>
                    <FaLink /> Generate Link
                  </Button>
                  {showIrccPackage && (
                    <Button
                      onClick={handleOpenPackageModal}
                      disabled={userForms.length === 0}
                      title="Generate IRCC submission package"
                      style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none', color: 'white' }}
                    >
                      <FaFileArchive /> Package
                    </Button>
                  )}
                </FormStats>
              </FormHeader>
              
              <QuestionsContainer>
                {formDetails?.sections?.map((section, sIdx) => (
                  <SectionCard key={sIdx}>
                    <SectionHeader onClick={() => toggleSection(section.title)}>
                      {expandedSections[section.title] ? <FaChevronDown /> : <FaChevronRight />}
                      <SectionTitle>{section.title}</SectionTitle>
                      <SectionProgress>
                        {section.answeredCount}/{section.questions?.length || 0}
                      </SectionProgress>
                    </SectionHeader>
                    <SectionContent $expanded={expandedSections[section.title]}>
                      {section.questions?.map((q, qIdx) => {
                        const answer = getAnswer(q.fieldName);
                        const isAnswered = answer && answer.trim() !== '';
                        
                        return (
                          <QuestionRow key={qIdx} $answered={isAnswered}>
                            <QuestionHeader>
                              <QuestionStatus $answered={isAnswered}>
                                {isAnswered ? <FaCheck /> : <IoTimeOutline />}
                              </QuestionStatus>
                              <QuestionText>{q.question}</QuestionText>
                            </QuestionHeader>
                            
                            {isAnswered && !pendingAnswers[q.fieldName] ? (
                              <AnswerDisplay onClick={() => handleAnswerChange(q.fieldName, answer)}>
                                <FaCheck /> {answer}
                              </AnswerDisplay>
                            ) : (
                              <AnswerInput
                                placeholder="Type answer here..."
                                value={pendingAnswers[q.fieldName] ?? answer}
                                onChange={(e) => handleAnswerChange(q.fieldName, e.target.value)}
                              />
                            )}
                            
                            <FieldName>Field: {q.fieldName}</FieldName>
                          </QuestionRow>
                        );
                      })}
                    </SectionContent>
                  </SectionCard>
                ))}
              </QuestionsContainer>
            </>
          )}
        </MainPanel>
      </Content>
      
      {/* Attach Form Modal */}
      {showAttachModal && (
        <ModalOverlay onClick={() => setShowAttachModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle>Attach Form to User</ModalTitle>
            
            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#9ca3af' }}>
              Select a form template. Existing user data will be automatically pre-filled.
            </div>
            
            {templates.map(template => {
              // Check if this form is already attached to this case
              const isAlreadyAttached = userForms.some(f => f.formTemplateId === template._id);
              
              return (
                <TemplateOption
                  key={template._id}
                  $selected={selectedTemplateIds.includes(template._id)}
                  onClick={() => !isAlreadyAttached && toggleTemplateSelected(template._id)}
                  style={isAlreadyAttached ? {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    background: 'rgba(31, 41, 55, 0.3)'
                  } : {}}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={selectedTemplateIds.includes(template._id)}
                      disabled={isAlreadyAttached}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => !isAlreadyAttached && toggleTemplateSelected(template._id)}
                      style={{ marginTop: '3px', width: '16px', height: '16px', cursor: isAlreadyAttached ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <TemplateName style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {template.formCode && (
                          <span style={{
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            background: 'rgba(59, 130, 246, 0.2)',
                            color: '#93c5fd',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>
                            {template.formCode}
                          </span>
                        )}
                        {template.name}
                        {isAlreadyAttached && (
                          <span style={{
                            fontSize: '10px',
                            background: 'rgba(99, 102, 241, 0.3)',
                            color: '#a5b4fc',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>
                            Already Attached
                          </span>
                        )}
                      </TemplateName>
                      <TemplateDetails>
                        {(template.totalFields ?? template.fieldCount ?? 0)} fields • {(template.totalQuestions ?? template.questionCount ?? 0)} questions
                      </TemplateDetails>
                    </div>
                  </div>
                </TemplateOption>
              );
            })}

            <ModalActions>
              <Button onClick={() => { setShowAttachModal(false); setSelectedTemplateIds([]); }}>Cancel</Button>
              <Button $primary onClick={handleAttachForms} disabled={selectedTemplateIds.length === 0 || attaching}>
                {attaching ? <LoadingSpinner /> : <FaPlus />}
                {attaching
                  ? 'Attaching…'
                  : `Attach ${selectedTemplateIds.length || ''} Form${selectedTemplateIds.length === 1 ? '' : 's'}`.replace('  ', ' ')}
              </Button>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}
      
      {/* Link Modal */}
      {showLinkModal && (
        <ModalOverlay onClick={() => setShowLinkModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle>Form Link Generated</ModalTitle>
            
            <div style={{ fontSize: '13px', color: '#9ca3af' }}>
              Share this link with the user. They can complete the remaining questions.
            </div>
            
            <LinkDisplay>
              <FaLink style={{ color: '#6366f1' }} />
              <LinkText value={generatedLink} readOnly />
              <CopyButton onClick={copyLink}>
                <FaCopy /> Copy
              </CopyButton>
            </LinkDisplay>
            
            <ModalActions>
              <Button onClick={() => setShowLinkModal(false)}>Close</Button>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}
      
      {/* IRCC Submission Package Modal */}
      {showPackageModal && (
        <ModalOverlay onClick={() => setShowPackageModal(false)}>
          <Modal onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <ModalTitle>
              <FaFileArchive style={{ marginRight: '10px', color: '#10b981' }} />
              Generate IRCC Submission Package
            </ModalTitle>
            
            <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>
              Select the application type to generate a complete submission-ready package with all forms and a checklist.
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#a5b4fc', marginBottom: '8px', textTransform: 'uppercase' }}>
                Application Type
              </div>
              {applicationTypes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                  <LoadingSpinner />
                </div>
              ) : (
                applicationTypes.map(appType => (
                  <TemplateOption
                    key={appType.typeId}
                    $selected={selectedAppType?.typeId === appType.typeId}
                    onClick={() => setSelectedAppType(appType)}
                  >
                    <TemplateName>{appType.name}</TemplateName>
                    <TemplateDetails>
                      {appType.requiredFormsCount} required forms • {appType.requiredDocsCount} required documents
                      {appType.estimatedProcessingTime && ` • ${appType.estimatedProcessingTime}`}
                    </TemplateDetails>
                  </TemplateOption>
                ))
              )}
            </div>
            
            {/* Forms selection */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#a5b4fc', textTransform: 'uppercase' }}>
                  Forms to include ({selectedFormIds.size}/{userForms.length})
                </div>
                {userForms.length > 0 && (
                  <button
                    onClick={() => setSelectedFormIds(selectedFormIds.size === userForms.length ? new Set() : new Set(userForms.map(f => f.sessionId)))}
                    style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: '11px', cursor: 'pointer' }}
                  >
                    {selectedFormIds.size === userForms.length ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>
              {userForms.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#6b7280', padding: '8px' }}>No forms attached to this case.</div>
              ) : (
                <div style={{ maxHeight: '140px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {userForms.map(f => (
                    <label key={f.sessionId} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: 'rgba(31,41,55,0.4)', border: '1px solid rgba(75,85,99,0.2)', borderRadius: '6px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedFormIds.has(f.sessionId)} onChange={() => toggleFormSelection(f.sessionId)} />
                      <span style={{ flex: 1, fontSize: '12px', color: '#e5e7eb' }}>{f.formName || f.templateName || f.name || 'Form'}</span>
                      {typeof f.completionPercent === 'number' && (
                        <span style={{ fontSize: '11px', color: f.completionPercent >= 100 ? '#10b981' : '#9ca3af' }}>{f.completionPercent}%</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Documents selection */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#a5b4fc', textTransform: 'uppercase' }}>
                  Documents to include ({selectedDocKeys.size}/{caseFiles.length})
                </div>
                {caseFiles.length > 0 && (
                  <button
                    onClick={() => setSelectedDocKeys(selectedDocKeys.size === caseFiles.length ? new Set() : new Set(caseFiles.map(f => f.fileName)))}
                    style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: '11px', cursor: 'pointer' }}
                  >
                    {selectedDocKeys.size === caseFiles.length ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>
              {loadingCaseFiles ? (
                <div style={{ textAlign: 'center', padding: '12px' }}><LoadingSpinner /></div>
              ) : caseFiles.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#6b7280', padding: '8px' }}>No documents uploaded to this case.</div>
              ) : (
                <div style={{ maxHeight: '140px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {caseFiles.map(f => (
                    <label key={f.fileName} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: 'rgba(31,41,55,0.4)', border: '1px solid rgba(75,85,99,0.2)', borderRadius: '6px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedDocKeys.has(f.fileName)} onChange={() => toggleDocSelection(f.fileName)} />
                      <span style={{ flex: 1, fontSize: '12px', color: '#e5e7eb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.originalName}</span>
                      <span style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase' }}>{f.fileType}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '16px' }}>
              The ZIP includes a <strong>Forms/</strong> folder, <strong>Supporting_Documents/</strong> folder, plus a cover letter, checklist and README.
            </div>

            <ModalActions>
              <Button onClick={() => setShowPackageModal(false)}>Cancel</Button>
              <Button
                $primary
                onClick={handleGeneratePackage}
                disabled={!selectedAppType || generatingPackage || (selectedFormIds.size === 0 && selectedDocKeys.size === 0)}
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
              >
                {generatingPackage ? <LoadingSpinner /> : <FaDownload />}
                {generatingPackage ? 'Generating...' : 'Download Package'}
              </Button>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}
      
      {/* Detach Form Confirmation Modal */}
      {showDetachModal && formToDetach && (
        <ModalOverlay onClick={() => !detaching && setShowDetachModal(false)}>
          <ConfirmationModal onClick={e => e.stopPropagation()}>
            <WarningIcon>
              <FaExclamationTriangle />
            </WarningIcon>
            
            <ConfirmTitle>Detach Form?</ConfirmTitle>
            
            <ConfirmText>
              Are you sure you want to detach <FormNameHighlight>"{formToDetach.formName || 'this form'}"</FormNameHighlight> from this case?
            </ConfirmText>
            
            <ConfirmText style={{ fontSize: '12px', marginTop: '12px' }}>
              The form will be removed from this conversation. Any collected data will remain in the user's profile.
            </ConfirmText>
            
            <ModalActions style={{ marginTop: '24px' }}>
              <Button 
                onClick={() => setShowDetachModal(false)} 
                disabled={detaching}
              >
                Cancel
              </Button>
              <DangerButton 
                onClick={handleConfirmDetach} 
                disabled={detaching}
              >
                {detaching ? <LoadingSpinner /> : <FaTrashAlt />}
                {detaching ? 'Detaching...' : 'Yes, Detach Form'}
              </DangerButton>
            </ModalActions>
          </ConfirmationModal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default UserFormsPanel;

